import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { PlusOutlined, TagsOutlined, ReloadOutlined } from '@ant-design/icons';
import { getAllOfertas, getAllOfertasActivas, postOfertas, updateOferta, deleteOferta } from '../../../store/MSCommerce/Ofertas/thunks';
import { getProducts } from '../../../store/MSCommerce/Productos/thunks';
import { GetAllCategorys } from '../../../store/MSCommerce/Categories/thunks';
import type { OfertaResponse, OfertaRequest, UpdateOfertaRequest } from '../../../store/MSCommerce/Ofertas/types';
import type { ProductosResponse } from '../../../store/MSCommerce/Productos/types';
import type { CategoryResponse } from '../../../store/MSCommerce/Categories/types';
import { AppState } from '../../../store';
import { isAdmin } from '../../../Services/auth';
import { OfertaCard } from './components/OfertaCard';
import { CreateOfertaModal } from './components/CreateOfertaModal';
import { UpdateOfertaModal } from './components/UpdateOfertaModal';
import styles from './Ofertas.module.scss';

interface OfertasProps {
  getAllOfertas?: () => void;
  getAllOfertasActivas?: () => void;
  postOfertas?: (data: OfertaRequest) => void;
  updateOferta?: (data: UpdateOfertaRequest) => void;
  deleteOferta?: (id: string) => void;
  getProducts?: (categoria?: string, nombre?: string) => void;
  GetAllCategorys?: () => void;
  ofertas?: OfertaResponse[];
  productos?: ProductosResponse[];
  categorias?: CategoryResponse[];
}

const Ofertas: React.FC<OfertasProps> = ({
  getAllOfertas,
  getAllOfertasActivas,
  postOfertas,
  updateOferta,
  deleteOferta,
  getProducts,
  GetAllCategorys,
  ofertas,
  productos,
  categorias,
}) => {
  const admin = isAdmin();
  const [showCreate, setShowCreate] = useState(false);
  const [ofertaToEdit, setOfertaToEdit] = useState<OfertaResponse | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const cargarOfertas = () => {
    if (admin) {
      if (getAllOfertas) getAllOfertas();
    } else {
      if (getAllOfertasActivas) getAllOfertasActivas();
    }
  };

  useEffect(() => {
    cargarOfertas();
    if (admin) {
      if (getProducts) getProducts();
      if (GetAllCategorys) GetAllCategorys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    cargarOfertas();
    setTimeout(() => setRefreshing(false), 800);
  };

  const handleCreate = (data: OfertaRequest) => {
    if (postOfertas) {
      postOfertas(data);
      setShowCreate(false);
      setTimeout(cargarOfertas, 500);
    }
  };

  const handleUpdate = (data: UpdateOfertaRequest) => {
    if (updateOferta) {
      updateOferta(data);
      setOfertaToEdit(null);
      setTimeout(cargarOfertas, 500);
    }
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('¿Eliminar esta oferta?')) return;
    if (deleteOferta) {
      deleteOferta(id);
      setTimeout(cargarOfertas, 500);
    }
  };

  const lista = ofertas ?? [];

  return (
    <div className={styles.ofertas}>

      <div className={styles.ofertas__header}>
        <div>
          <p className={styles.ofertas__subtitle}>GLOW SKIN</p>
          <h1 className={styles.ofertas__title}><TagsOutlined /> Ofertas</h1>
        </div>

        <div className={styles.ofertas__headerActions}>
          <button
            className={`${styles.ofertas__createBtn} ${styles['ofertas__createBtn--outline']}`}
            onClick={handleRefresh}
          >
            <ReloadOutlined spin={refreshing} /> Recargar
          </button>

          {admin && (
            <button className={styles.ofertas__createBtn} onClick={() => setShowCreate(true)}>
              <PlusOutlined /> Nueva Oferta
            </button>
          )}
        </div>
      </div>

      <div className={styles.ofertas__grid}>
        {lista.length > 0 ? (
          lista.map(oferta => (
            <OfertaCard
              key={oferta.id}
              oferta={oferta}
              isAdmin={admin}
              onEdit={setOfertaToEdit}
              onDelete={handleDelete}
            />
          ))
        ) : (
          <p className={styles.ofertas__empty}>Por ahora no hay ofertas activas. ¡Vuelve pronto!</p>
        )}
      </div>

      {showCreate && (
        <CreateOfertaModal
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
          productos={productos}
          categorias={categorias}
        />
      )}

      {ofertaToEdit && (
        <UpdateOfertaModal
          oferta={ofertaToEdit}
          onClose={() => setOfertaToEdit(null)}
          onSubmit={handleUpdate}
        />
      )}

    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  ofertas: state.ofertasAction.ofertas,
  productos: state.productosAction.products,
  categorias: state.categoryAction.categories,
});

export default connect(mapStateToProps, {
  getAllOfertas,
  getAllOfertasActivas,
  postOfertas,
  updateOferta,
  deleteOferta,
  getProducts,
  GetAllCategorys,
})(Ofertas);
